import { createSelector } from 'reselect'

import { RootState } from 'redux/types'

import { VoucherError, VoucherState, Voucher, Offer } from '../types'

const getVouchersState = (state: RootState): VoucherState => state.vouchers

const getConfiguredOffers = createSelector(
  getVouchersState,
  (voucherState: VoucherState) => voucherState.configuredOffers,
)

const getAppliedVouchers = createSelector(
  getVouchersState,
  (voucherState: VoucherState) => voucherState.appliedVouchers,
)

const getUnAppliedVouchers = createSelector(
  getVouchersState,
  (voucherState: VoucherState) => voucherState.unAppliedVouchers,
)

const getErrors = createSelector(
  getVouchersState,
  (voucherState: VoucherState) => voucherState.errors,
)

const getDiscountError = createSelector(getErrors, (errors: VoucherError[]) =>
  errors.find((error: VoucherError) => error.target === 'couponCode'),
)
const getOfferError = createSelector(getErrors, (errors: VoucherError[]) =>
  errors.find((error: VoucherError) => error.target === 'voucherCode'),
)

const getPostVouchersStatus = createSelector(
  getVouchersState,
  (vouchersState: VoucherState) => vouchersState.postVouchersStatus,
)

const getGetVouchersStatus = createSelector(
  getVouchersState,
  (vouchersState: VoucherState) => vouchersState.getVouchersStatus,
)

const getDeleteVouchersStatus = createSelector(
  getVouchersState,
  (vouchersState: VoucherState) => vouchersState.deleteVouchersStatus,
)

const getMaxVouchersCount = createSelector(
  getVouchersState,
  (vouchersState: VoucherState) => vouchersState.maxVouchers,
)

const getIsOnlineVoucherAvailable = createSelector(
  getAppliedVouchers,
  (appliedVouchers: Voucher[]) =>
    appliedVouchers?.filter((voucher) => voucher.onlineVoucher)?.length > 0,
)

const getOffers = createSelector(
  getVouchersState,
  (vouchersState: VoucherState): Offer[] => vouchersState.offers,
)

const getAppliedOffers = createSelector(getAppliedVouchers, (appliedVouchers: Voucher[]) =>
  appliedVouchers.filter((voucher) => voucher.onlineVoucher),
)

const getUnAppliedOffers = createSelector(
  getUnAppliedVouchers,
  getOffers,
  (unAppliedVouchers: VoucherError[], offers: Offer[]) =>
    unAppliedVouchers.filter((error) =>
      offers.find((offer) => offer.voucherCode === error.details),
    ),
)

const getAppliedDiscounts = createSelector(getAppliedVouchers, (appliedVouchers: Voucher[]) =>
  appliedVouchers.filter((voucher) => !voucher.onlineVoucher),
)

const getUnAppliedDiscounts = createSelector(
  getUnAppliedVouchers,
  getAppliedVouchers,
  (unAppliedVouchers: VoucherError[], appliedVouchers: Voucher[]) =>
    unAppliedVouchers.filter((error) =>
      appliedVouchers.find((appliedVoucher) => appliedVoucher.voucherCode === error.details),
    ),
)

const getAppliedVouchersCount = createSelector(
  getAppliedVouchers,
  (appliedVouchers: Voucher[]) => appliedVouchers.length,
)

const getIsMaxVouchersReached = createSelector(
  getAppliedVouchersCount,
  getMaxVouchersCount,
  (allAppliedCount: number, maxVouchersCount: number) => allAppliedCount >= maxVouchersCount,
)

const getDeletedVouchers = createSelector(
  getVouchersState,
  (vouchersState: VoucherState) => vouchersState.deletedVouchers,
)

export const getLastAppliedUnavailableVoucher = createSelector(
  getVouchersState,
  (voucherState: VoucherState) => voucherState.lastAppliedUnavailableVoucher,
)

export const getPostingVoucherWithCode = createSelector(
  getVouchersState,
  (voucherState: VoucherState) => voucherState.postingVoucherWithCode,
)

export default {
  getUnAppliedOffers,
  getUnAppliedDiscounts,
  getAppliedVouchers,
  getUnAppliedVouchers,
  getPostVouchersStatus,
  getGetVouchersStatus,
  getDeleteVouchersStatus,
  getErrors,
  getDiscountError,
  getOfferError,
  getOffers,
  getIsOnlineVoucherAvailable,
  getAppliedOffers,
  getAppliedDiscounts,
  getMaxVouchersCount,
  getAppliedVouchersCount,
  getIsMaxVouchersReached,
  getConfiguredOffers,
  getDeletedVouchers,
}
